import { put, take, fork, select, call, cancel, delay, race, takeLatest, } from 'typed-redux-saga/macro';
import { buffers, channel, eventChannel } from 'redux-saga';
import { AppState } from 'react-native';
import NetInfo from '@react-native-community/netinfo';
import { resetError, setNetworkState } from 'actions';
import { SET_APP_STATE_STATUS } from 'actions/appState';
/**
 * NetInfo reports the internet as unreachable for a few seconds after the app
 * resumes, because iOS cuts off its reachability request while suspended.
 */
export const UNREACHABLE_GRACE_PERIOD_MS = 10000;
const isAppStateAction = (isActive) => (action) => action.type === SET_APP_STATE_STATUS &&
    (action.payload === 'active') === isActive;
/**
 * Waits for the grace period to pass while the app is in the foreground. A delay
 * started in the background would fire as soon as the app resumes.
 */
function* waitForForegroundGracePeriod() {
    while (true) {
        if (AppState.currentState !== 'active') {
            yield* take(isAppStateAction(true));
        }
        const { elapsed } = yield* race({
            elapsed: delay(UNREACHABLE_GRACE_PERIOD_MS),
            backgrounded: take(isAppStateAction(false)),
        });
        if (elapsed) {
            return;
        }
    }
}
const toNetworkState = (netInfoState) => ({
    isConnected: netInfoState.isConnected ?? true,
    isInternetReachable: netInfoState.isInternetReachable ?? true,
});
function* applyNetworkState(networkState) {
    const { isConnected, isInternetReachable } = networkState;
    const currentNetworkState = yield* select((store) => store.network);
    if (isConnected === currentNetworkState.isConnected &&
        isInternetReachable === currentNetworkState.isInternetReachable) {
        return;
    }
    if (isConnected &&
        !isInternetReachable &&
        currentNetworkState.isInternetReachable) {
        yield* call(waitForForegroundGracePeriod);
    }
    yield* put(setNetworkState({ isConnected, isInternetReachable }));
    if (isConnected) {
        yield* put(resetError());
    }
}
function* watchNetworkStates(updates) {
    let pending;
    while (true) {
        const networkState = toNetworkState(yield* take(updates));
        const isAlreadyPending = pending?.task.isRunning() &&
            pending.isConnected === networkState.isConnected &&
            pending.isInternetReachable === networkState.isInternetReachable;
        if (!isAlreadyPending) {
            if (pending) {
                yield* cancel(pending.task);
            }
            const task = yield* fork(applyNetworkState, networkState);
            pending = { ...networkState, task };
        }
    }
}
function* forwardNetInfoEvents(updates) {
    const events = eventChannel((emitter) => {
        const unsubscribe = NetInfo.addEventListener((state) => {
            emitter(state);
        });
        return () => {
            unsubscribe();
        };
    }, buffers.sliding(1));
    while (true) {
        const netInfoState = yield* take(events);
        yield* put(updates, netInfoState);
    }
}
function* fetchNetInfoState(updates) {
    const netInfoState = yield* call(NetInfo.fetch);
    yield* put(updates, netInfoState);
}
function* onAppState(updates, appStateAction) {
    if (appStateAction.payload === 'active') {
        yield* call(fetchNetInfoState, updates);
    }
}
function* netInfoSaga() {
    const updates = channel(buffers.expanding());
    yield* fork(watchNetworkStates, updates);
    yield* call(fetchNetInfoState, updates);
    yield* fork(forwardNetInfoEvents, updates);
    yield* takeLatest(SET_APP_STATE_STATUS, onAppState, updates);
}
export default netInfoSaga;
